import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { DialogButton, DialogTitleClose } from 'components';
import { useMutationCustom } from 'hooks';
import {
  PatientDocumentType,
  UploadPatientDocumentMutation,
  UploadPatientDocumentMutationVariables,
} from 'types.d';
import { UPLOAD_PATIENT_DOCUMENT } from 'gql';
import {
  ACCEPTED_EXTENSIONS,
  DOCUMENT_TYPE_LABEL,
  formatSize,
  MAX_DOCUMENT_BYTES,
} from './utils';

type Props = {
  open: boolean;
  patientId: string;
  onClose: () => void;
  onUploaded: () => void;
};

export const DialogUploadDocument: React.FC<Props> = ({
  open,
  patientId,
  onClose,
  onUploaded,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [type, setType] = useState<PatientDocumentType>(
    PatientDocumentType.Prescription,
  );
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');

  const [uploadDocument, { loading }] = useMutationCustom<
    UploadPatientDocumentMutation,
    UploadPatientDocumentMutationVariables
  >({
    api: UPLOAD_PATIENT_DOCUMENT,
    textSuccess: 'Document uploaded',
    callbackSuccess: () => {
      setFile(null);
      onUploaded();
    },
  });

  const pickFile = (picked?: File | null) => {
    setError('');
    setFile(null);
    if (!picked) return;
    const extension = `.${picked.name
      .split('.')
      .pop()
      ?.toLowerCase()}`;
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      setError(
        'Only PDF, JPG, PNG and Word (.doc, .docx) files can be uploaded',
      );
      return;
    }
    if (picked.size > MAX_DOCUMENT_BYTES) {
      setError('The file is larger than 20 MB');
      return;
    }
    if (!picked.size) {
      setError('The file is empty');
      return;
    }
    setFile(picked);
  };

  const upload = () => {
    if (!file) {
      setError('Choose a file to upload');
      return;
    }
    uploadDocument({ variables: { file, params: { patientId, type } } });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitleClose title="Upload document" onClose={onClose} />
      <DialogContent>
        <TextField
          select
          fullWidth
          size="small"
          variant="outlined"
          margin="dense"
          label="Type"
          value={type}
          onChange={e => setType(e.target.value as PatientDocumentType)}
        >
          {Object.values(PatientDocumentType).map(item => (
            <MenuItem key={item} value={item}>
              {DOCUMENT_TYPE_LABEL[item]}
            </MenuItem>
          ))}
        </TextField>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={ACCEPTED_EXTENSIONS.join(',')}
          data-testid="document-file-input"
          onChange={e => pickFile(e.target.files?.[0])}
        />
        <Box mt={2} display="flex" alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AttachFileIcon />}
            onClick={() => inputRef.current?.click()}
          >
            Choose file
          </Button>
          <Typography variant="body2" className="ml-8" noWrap>
            {file
              ? `${file.name} (${formatSize(file.size)})`
              : 'No file chosen'}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color={error ? 'error' : 'textSecondary'}
          component="p"
          style={{ marginTop: 8 }}
        >
          {error || 'PDF, JPG, PNG or Word, up to 20 MB'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <DialogButton isCancel onClickButton={onClose} />
        <DialogButton loading={loading} onClickButton={upload} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogUploadDocument;
